import React, { useMemo, useState } from 'react';
import { View, TouchableOpacity, StyleSheet, Modal, ScrollView } from 'react-native';
import { Text } from '@/components/common/text';
import { SVG_COMPONENTS } from '@/assets/admin';
import { IMessageResident } from '@/types/admin/messages';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { GlassCard } from '@/components/shared/redesign';

interface GroupChatConflictNoticeProps {
  mismatchedGuarantors?: IMessageResident[];
}

export const GroupChatConflictNotice: React.FC<GroupChatConflictNoticeProps> = ({ mismatchedGuarantors }) => {
  const { redesign } = useAdminColors();
  const [open, setOpen] = useState(false);

  const count = useMemo(() => mismatchedGuarantors?.length || 0, [mismatchedGuarantors]);
  if (!count) return null;

  return (
    <View style={styles.container}>
      <View
        style={[
          styles.banner,
          {
            backgroundColor: redesign.accent.orangeLight,
            borderColor: redesign.accent.orange,
          },
        ]}
      >
        <TouchableOpacity style={styles.headerRow} onPress={() => setOpen(true)} activeOpacity={0.8}>
          <Text style={[styles.title, { color: redesign.text.primary }]}>
            Group chat conflict
          </Text>
          <View style={styles.countPill}>
            <Text style={[styles.countText, { color: redesign.text.muted }]}>
              {count}
            </Text>
            <Text style={[styles.chevron, { color: redesign.text.muted }]}>
              {'>'}
            </Text>
          </View>
        </TouchableOpacity>
        <Text style={[styles.subtitle, { color: redesign.text.primary }]}>
          Due to privacy reasons, you can&apos;t add a resident&apos;s guarantors to a group chat with other residents. Guarantors will receive separate messages.
        </Text>
      </View>

      <Modal visible={open} animationType="fade" transparent={true} onRequestClose={() => setOpen(false)}>
        <View style={styles.modalBackdrop}>
          <GlassCard
            padding={0}
            style={[styles.modalCard, { backgroundColor: redesign.bg.modal }]}
          >
            <View
              style={[
                styles.modalHeader,
                { borderBottomColor: redesign.card.border },
              ]}
            >
              <Text style={[styles.modalTitle, { color: redesign.text.primary }]}>
                Conflict details
              </Text>
              <TouchableOpacity onPress={() => setOpen(false)}>
                <SVG_COMPONENTS.X_CIRCLE width={20} height={20} color={redesign.text.muted} />
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.modalBody} contentContainerStyle={styles.modalBodyContent}>
              <Text style={[styles.modalIntro, { color: redesign.text.primary }]}>
                The following guarantors cannot be added to the group chat with other residents.
              </Text>
              <View
                style={[
                  styles.listWrap,
                  { borderColor: redesign.card.border },
                ]}
              >
                {mismatchedGuarantors?.map((g) => {
                  const lc = g.leaseCustomers?.[0] as any;
                  const displayName = lc?.first_name ? `${lc.first_name} ${lc.last_name}` : lc?.email;
                  const status = (g.statusType || '').toString();
                  const statusFormatted = status ? `${status.charAt(0).toUpperCase()}${status.slice(1)}` : '';
                  const initials = (lc?.first_name?.[0] || lc?.email?.[0] || '?').toUpperCase();
                  return (
                    <View key={g.id} style={styles.itemRow}>
                      <View
                        style={[
                          styles.simpleAvatar,
                          { backgroundColor: redesign.card.bg },
                        ]}
                      >
                        <Text scale="micro" style={[styles.simpleAvatarText, { color: redesign.text.primary }]}>
                          {initials}
                        </Text>
                      </View>
                      <View style={styles.itemTextWrap}>
                        <Text style={[styles.itemTitle, { color: redesign.text.primary }]} numberOfLines={lc?.first_name ? 2 : 1} ellipsizeMode={lc?.first_name ? 'tail' : 'middle'}>
                          {displayName}
                        </Text>
                        <Text style={[styles.itemSubtitle, { color: redesign.text.muted }]} numberOfLines={1}>
                          Guarantor - {statusFormatted}
                        </Text>
                      </View>
                    </View>
                  );
                })}
              </View>
            </ScrollView>
          </GlassCard>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingTop: 8,
    marginBottom: 16,
  },
  banner: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 12,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 6,
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
  },
  countPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  countText: {
    fontSize: 14,
  },
  chevron: {
    fontSize: 16,
    marginLeft: 2,
  },
  subtitle: {
    fontSize: 14,
    lineHeight: 18,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    padding: 16,
  },
  modalCard: {
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: '700',
  },
  modalBody: {
    maxHeight: 420,
    minWidth: 320,
  },
  modalBodyContent: {
    padding: 16,
  },
  modalIntro: {
    fontSize: 14,
    marginBottom: 12,
  },
  listWrap: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    gap: 12,
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  itemTextWrap: {
    flex: 1,
  },
  simpleAvatar: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  simpleAvatarText: {
    fontSize: 12,
    fontWeight: '700',
  },
  itemTitle: {
    fontSize: 14,
    fontWeight: '600',
  },
  itemSubtitle: {
    fontSize: 12,
  },
});

export default GroupChatConflictNotice;
